/** Renders a PDF's pages in the browser with an overlay layer for signature boxes. */
import { getDocument, GlobalWorkerOptions, type PDFDocumentProxy } from "pdfjs-dist";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Loading } from "@/components/page";

GlobalWorkerOptions.workerSrc = workerUrl;

export function usePdf(url: string | null) {
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    setDoc(null);
    setError(null);
    const task = getDocument({ url, withCredentials: true, isEvalSupported: false });
    task.promise.then(
      (d) => !cancelled && setDoc(d),
      (e: Error & { status?: number }) =>
        !cancelled &&
        setError(
          e?.status === 402
            ? "This document will open once the invoice is paid."
            : e?.name === "PasswordException"
              ? "This PDF is password-protected."
              : "We couldn't open this PDF.",
        ),
    );
    return () => {
      cancelled = true;
      void task.destroy();
    };
  }, [url]);
  return { doc, error };
}

function Page({ doc, index, width, overlay, onClick }: { doc: PDFDocumentProxy; index: number; width: number; overlay?: ReactNode; onClick?: (x: number, y: number) => void }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [height, setHeight] = useState(width * 1.294);
  useEffect(() => {
    let cancelled = false;
    let task: { cancel: () => void; promise: Promise<void> } | null = null;
    doc.getPage(index + 1).then((page) => {
      if (cancelled || !canvas.current) return;
      const base = page.getViewport({ scale: 1 });
      const ratio = window.devicePixelRatio || 1;
      const viewport = page.getViewport({ scale: (width / base.width) * ratio });
      const c = canvas.current;
      c.width = viewport.width;
      c.height = viewport.height;
      setHeight((viewport.height / ratio));
      task = page.render({ canvasContext: c.getContext("2d")!, viewport });
      task.promise.catch(() => {});
    });
    return () => {
      cancelled = true;
      task?.cancel();
    };
  }, [doc, index, width]);
  return (
    <div
      className="relative mx-auto bg-white shadow-xs ring-1 ring-border"
      style={{ width, height }}
      onClick={(e) => {
        if (!onClick || e.target !== e.currentTarget.querySelector("canvas")) return;
        const r = e.currentTarget.getBoundingClientRect();
        onClick((e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height);
      }}
    >
      <canvas ref={canvas} className="block h-full w-full" aria-label={`Page ${index + 1}`} />
      <div className="pointer-events-none absolute inset-0 *:pointer-events-auto">{overlay}</div>
    </div>
  );
}

/**
 * All pages, stacked. `overlay(page)` draws boxes positioned with fractions of
 * the page (left/top/width/height in %), matching how the server stamps them.
 */
export function PdfPages({
  url,
  overlay,
  onPageClick,
  onLoaded,
  onError,
  maxWidth = 760,
}: {
  url: string;
  overlay?: (page: number) => ReactNode;
  onPageClick?: (page: number, x: number, y: number) => void;
  /** Called once the PDF is open, with its page count. */
  onLoaded?: (pages: number) => void;
  onError?: (message: string) => void;
  maxWidth?: number;
}) {
  const { doc, error } = usePdf(url);
  useEffect(() => {
    if (error) onError?.(error);
  }, [error]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (doc) onLoaded?.(doc.numPages);
  }, [doc]); // eslint-disable-line react-hooks/exhaustive-deps
  const wrap = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(maxWidth);
  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(Math.min(maxWidth, el.clientWidth)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [maxWidth]);
  return (
    <div ref={wrap} className="w-full">
      {error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : !doc ? (
        <Loading />
      ) : (
        <div className="space-y-3">
          {Array.from({ length: doc.numPages }, (_, i) => (
            <div key={i}>
              <p className="mb-1 text-center text-xs text-muted-foreground">
                Page {i + 1} of {doc.numPages}
              </p>
              <Page doc={doc} index={i} width={width} overlay={overlay?.(i)} onClick={onPageClick ? (x, y) => onPageClick(i, x, y) : undefined} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Absolutely positions a child on a page using fractions of the page. */
export function boxStyle(b: { x: number; y: number; width: number; height: number }) {
  return { left: `${b.x * 100}%`, top: `${b.y * 100}%`, width: `${b.width * 100}%`, height: `${b.height * 100}%` };
}
